import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTabData } from '../hooks/useTabData';
import { fetchWinners } from '../services/apiService';
import { SkeletonLoader } from './SkeletonLoader';
import { getCountryName } from '../config/countryNames';
import { getDisciplineName } from '../config/disciplineNames';

const FLAG_ICON_MODULES = import.meta.glob('../assets/images/icons/national-flag-*.svg', {
  eager: true,
  import: 'default'
});

const DEFAULT_FLAG_MODULE = import.meta.glob('../assets/images/icons/icon-default.svg', {
  eager: true,
  import: 'default'
});

const FLAG_ICONS_BY_CODE = Object.entries(FLAG_ICON_MODULES).reduce((acc, [filePath, assetUrl]) => {
  const match = filePath.match(/national-flag-([a-z0-9-]+)\.svg$/);
  if (!match) return acc;
  acc[match[1].toUpperCase()] = assetUrl;
  return acc;
}, {});

const DEFAULT_FLAG_ICON = Object.values(DEFAULT_FLAG_MODULE)[0] || '';

const MEDAL_TYPES = ['Gold', 'Silver', 'Bronze'];

const normalizeMedalType = (value) => {
  const raw = (value || '').toString().trim().toLowerCase();
  if (raw.includes('gold') || raw === 'g') return 'Gold';
  if (raw.includes('silver') || raw === 's') return 'Silver';
  if (raw.includes('bronze') || raw === 'b') return 'Bronze';
  return 'Other';
};

const formatWhen = (isoDate) => {
  if (!isoDate) return 'TBC';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return 'TBC';

  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short'
  });
};

const isAustralian = (row) => {
  if ((row.countryCode || '').toUpperCase() === 'AUS') return true;

  return (row.competitors || []).some((competitor) => {
    const competitorCode = (competitor?.OrganisationCode || '').toString().trim().toUpperCase();
    return competitorCode === 'AUS';
  });
};

const getGroupKey = (row) => {
  return [
    row.eventId || '',
    row.eventCode || '',
    row.eventName || '',
    row.phaseName || '',
    row.discipline || '',
    row.dateTime || ''
  ].join('|');
};

const normalizeLabel = (value) => {
  return (value || '').toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
};

const getWinnerDisplay = (winner) => {
  const winnerName = (winner?.winnerName || '').toString().trim();
  const countryName = (winner?.countryName || '').toString().trim();
  const countryCode = (winner?.countryCode || '').toString().trim().toUpperCase();

  const normalizedWinner = normalizeLabel(winnerName);
  const normalizedCountry = normalizeLabel(countryName);
  const normalizedCode = normalizeLabel(countryCode);
  const isUnknownWinner = !normalizedWinner || normalizedWinner === normalizeLabel('Unknown winner');
  const isCountryLikeWinner =
    normalizedWinner && (
      normalizedWinner === normalizedCountry ||
      normalizedWinner === normalizedCode
    );

  if ((isUnknownWinner || isCountryLikeWinner) && (countryName || countryCode)) {
    return {
      primaryText: countryName || countryCode,
      showCountryLine: false
    };
  }

  return {
    primaryText: winnerName || countryName || countryCode || 'Unknown winner',
    showCountryLine: Boolean(countryName || countryCode)
  };
};

const buildWinnerName = (entry) => {
  const teamName = (entry?.TeamName || entry?.TVTeamName || '').toString().trim();
  if (teamName) return teamName;

  const personName = [entry?.GivenName, entry?.FamilyName]
    .map((part) => (part || '').toString().trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  if (personName) return personName;

  return (
    entry?.PrintName ||
    entry?.TVName ||
    entry?.TVInitialName ||
    entry?.FamilyName ||
    'Unknown winner'
  );
};

const sortWinners = (a, b) => {
  const countryCompare = (a.countryName || '').localeCompare(b.countryName || '');
  if (countryCompare !== 0) return countryCompare;

  const winnerCompare = (a.winnerName || '').localeCompare(b.winnerName || '');
  if (winnerCompare !== 0) return winnerCompare;

  return (a.countryCode || '').localeCompare(b.countryCode || '');
};

const getFlagIconSrc = (countryCode) => {
  if (!countryCode) return DEFAULT_FLAG_ICON;
  return FLAG_ICONS_BY_CODE[countryCode.toUpperCase()] || DEFAULT_FLAG_ICON;
};

export const Winners = ({ isActive }) => {
  const { data, isLoading, error, refetch } = useTabData('winners', fetchWinners);
  const [scope, setScope] = useState('all');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const winnersListRef = useRef(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.flatMap((entry, entryIndex) => {
      const events = Array.isArray(entry?.Events) ? entry.Events : [];
      const winnerName = buildWinnerName(entry);
      const countryCode = (entry?.OrganisationCode || '').toString().trim().toUpperCase();
      const countryName = getCountryName(countryCode || '');

      return events.map((event, eventIndex) => {
        const medalType = normalizeMedalType(event?.MedalType || event?.Type || event?.ResultStatus);
        const eventDateTime = event?.UnitDateTime || event?.MedalDate || '';
        const sortTime = eventDateTime ? Date.parse(eventDateTime) : NaN;

        return {
          id: `${entry?.Id || 'w'}-${entryIndex}-${event?.Id || eventIndex}`,
          eventId: event?.EventCode || event?.MedalUnitCode || '',
          winnerName,
          countryCode,
          countryName,
          discipline: getDisciplineName(event?.DisciplineCode || ''),
          competitors: Array.isArray(event?.Competitors) ? event.Competitors : [],
          medalType,
          eventName: event?.EventDescription || event?.EventLongDescription || 'Event',
          phaseName: event?.ResultStatus || '',
          eventCode: event?.EventCode || event?.MedalUnitCode || '',
          dateTime: eventDateTime,
          sortTime: Number.isNaN(sortTime) ? 0 : sortTime
        };
      });
    });
  }, [data]);

  const availableDisciplines = useMemo(() => {
    const unique = Array.from(new Set(rows.map((row) => row.discipline).filter(Boolean)));
    return unique.sort((a, b) => a.localeCompare(b));
  }, [rows]);

  useEffect(() => {
    if (disciplineFilter === 'all') return;
    if (availableDisciplines.includes(disciplineFilter)) return;
    setDisciplineFilter('all');
  }, [availableDisciplines, disciplineFilter]);

  const groupedEvents = useMemo(() => {
    const filteredRows = disciplineFilter === 'all'
      ? rows
      : rows.filter((row) => row.discipline === disciplineFilter);

    const groupedMap = filteredRows.reduce((acc, row) => {
      const key = getGroupKey(row);
      if (!acc[key]) {
        acc[key] = {
          id: key,
          eventName: row.eventName,
          phaseName: row.phaseName,
          eventCode: row.eventCode,
          discipline: row.discipline,
          dateTime: row.dateTime,
          sortTime: row.sortTime,
          medals: {
            Gold: [],
            Silver: [],
            Bronze: [],
            Other: []
          }
        };
      }

      const bucket = acc[key].medals[row.medalType] ? row.medalType : 'Other';
      acc[key].medals[bucket].push(row);
      acc[key].sortTime = Math.max(acc[key].sortTime, row.sortTime);
      return acc;
    }, {});

    const sortedGroups = Object.values(groupedMap).sort((a, b) => b.sortTime - a.sortTime);
    if (scope !== 'aus') return sortedGroups;

    return sortedGroups.filter((group) => {
      const medalRows = [
        ...group.medals.Gold,
        ...group.medals.Silver,
        ...group.medals.Bronze,
        ...group.medals.Other
      ];

      return medalRows.some(isAustralian);
    });
  }, [rows, disciplineFilter, scope]);

  const visibleMedalCount = useMemo(() => {
    if (!groupedEvents.length) return 0;

    return groupedEvents.reduce((total, group) => {
      return total + MEDAL_TYPES.reduce((sum, medalType) => sum + group.medals[medalType].length, 0);
    }, 0);
  }, [groupedEvents]);

  useEffect(() => {
    const listEl = winnersListRef.current;
    if (!listEl) return undefined;

    const updateScrollState = () => {
      const maxScrollTop = listEl.scrollHeight - listEl.clientHeight;
      if (maxScrollTop <= 1) {
        setHasOverflow(false);
        setCanScrollUp(false);
        setCanScrollDown(false);
        return;
      }

      setHasOverflow(true);
      setCanScrollUp(listEl.scrollTop > 1);
      setCanScrollDown(listEl.scrollTop < maxScrollTop - 1);
    };

    const rafId = window.requestAnimationFrame(updateScrollState);
    const settleTimeoutId = window.setTimeout(updateScrollState, 160);
    let resizeObserver;

    if (typeof window.ResizeObserver !== 'undefined') {
      resizeObserver = new window.ResizeObserver(updateScrollState);
      resizeObserver.observe(listEl);
    }

    const stopTouchPropagation = (event) => {
      event.stopPropagation();
    };

    listEl.addEventListener('scroll', updateScrollState, { passive: true });
    listEl.addEventListener('touchstart', stopTouchPropagation, { passive: true });
    listEl.addEventListener('touchmove', stopTouchPropagation, { passive: true });
    window.addEventListener('resize', updateScrollState);
    window.addEventListener('orientationchange', updateScrollState);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(settleTimeoutId);
      listEl.removeEventListener('scroll', updateScrollState);
      listEl.removeEventListener('touchstart', stopTouchPropagation);
      listEl.removeEventListener('touchmove', stopTouchPropagation);
      window.removeEventListener('resize', updateScrollState);
      window.removeEventListener('orientationchange', updateScrollState);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [groupedEvents.length, scope, disciplineFilter, isActive]);

  const nudgeWinnersList = (direction) => {
    const listEl = winnersListRef.current;
    if (!listEl) return;

    const step = Math.max(140, Math.round(listEl.clientHeight * 0.55));
    listEl.scrollBy({
      top: direction * step,
      behavior: 'smooth'
    });
  };

  const hasScrollableWinners = hasOverflow;

  if (error) {
    return (
      <div className="tab-content error-state">
        <p className="error-message">Failed to load winners</p>
        <button className="retry-button" onClick={refetch}>Retry</button>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="tab-content loading-state">
        <SkeletonLoader count={8} type="card" />
      </div>
    );
  }

  return (
    <div className="tab-content winners">
      <div className="winners-controls">
        <div className="winners-controls__group" role="tablist" aria-label="Winners scope">
          <button
            className={`winners-controls__button ${scope === 'all' ? 'is-active' : ''}`}
            onClick={() => setScope('all')}
            type="button"
          >
            All Winners
          </button>
          <button
            className={`winners-controls__button ${scope === 'aus' ? 'is-active' : ''}`}
            onClick={() => setScope('aus')}
            type="button"
          >
            Australia
          </button>
        </div>

        <div className="winners-controls__group" role="tablist" aria-label="Discipline filter">
          <button
            className={`winners-controls__button ${disciplineFilter === 'all' ? 'is-active' : ''}`}
            onClick={() => setDisciplineFilter('all')}
            type="button"
          >
            All Sports
          </button>
          {availableDisciplines.map((discipline) => {
            return (
              <button
                key={discipline}
                className={`winners-controls__button ${disciplineFilter === discipline ? 'is-active' : ''}`}
                onClick={() => setDisciplineFilter(discipline)}
                type="button"
              >
                {discipline}
              </button>
            );
          })}
        </div>
      </div>

      <p className="winners-summary">Showing {groupedEvents.length} events · {visibleMedalCount} medal results</p>

      <div className="winners-list" ref={winnersListRef}>
        {groupedEvents.length === 0 ? (
          <div className="no-events">
            <p>No winners found for this filter.</p>
          </div>
        ) : (
          groupedEvents.map((group) => (
            <article key={group.id} className="winner-card">
              <div className="winner-card__top">
                <div className="winner-meta">
                  <span>{group.discipline}</span>
                </div>
                <span className="winner-time">{formatWhen(group.dateTime)}</span>
              </div>

              <h3 className="winner-event">{group.eventName}</h3>

              <div className="winner-medal-grid">
                {MEDAL_TYPES.map((medalType) => {
                  const winners = [...group.medals[medalType]].sort(sortWinners);

                  return (
                    <div key={`${group.id}-${medalType}`} className="winner-medal-row">
                      <span className={`winner-medal winner-medal--${medalType.toLowerCase()}`}>
                        <span className={`winner-medal-icon winner-medal-icon--${medalType.toLowerCase()}`} aria-hidden="true" />
                        {medalType}
                      </span>
                      <div className="winner-medal-winners">
                        {winners.length === 0 ? (
                          <span className="winner-medal-empty">No medal awarded</span>
                        ) : (
                          winners.map((winner, winnerIndex) => (
                            <div key={`${group.id}-${medalType}-${winnerIndex}`} className="winner-medal-entry">
                              {winner.countryCode && (
                                <img
                                  src={getFlagIconSrc(winner.countryCode)}
                                  alt=""
                                  aria-hidden="true"
                                  className="winner-entry-flag"
                                  loading="lazy"
                                  decoding="async"
                                />
                              )}
                              {(() => {
                                const display = getWinnerDisplay(winner);

                                return (
                                  <div className="winner-entry-copy">
                                    <span className="winner-name">{display.primaryText}</span>
                                    {display.showCountryLine && (
                                      <span className="winner-country">{winner.countryName}</span>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          ))
        )}
      </div>

      <div className={`schedule-scroll-bar schedule-scroll-bar--controls-only ${canScrollDown ? 'schedule-scroll-bar--has-below' : ''}`.trim()}>
        {hasScrollableWinners && (
          <div className="schedule-scroll-bar__controls">
            <button
              type="button"
              className="schedule-scroll-bar__btn"
              onClick={() => nudgeWinnersList(-1)}
              disabled={!canScrollUp}
              aria-label="Scroll winners list up"
            >
              <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M8 3L14 11H2Z" />
              </svg>
            </button>
            <button
              type="button"
              className="schedule-scroll-bar__btn"
              onClick={() => nudgeWinnersList(1)}
              disabled={!canScrollDown}
              aria-label="Scroll winners list down"
            >
              <svg className="schedule-scroll-bar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M8 13L2 5h12Z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
